import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Shield, Clock, CheckCircle2 } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const STATUS_META = {
  open: { icon: AlertTriangle, tone: "text-amber-700 bg-amber-50 border-amber-200", label: "Open" },
  awaiting_customer: { icon: Clock, tone: "text-amber-700 bg-amber-50 border-amber-200", label: "Awaiting customer" },
  awaiting_provider: { icon: Clock, tone: "text-amber-700 bg-amber-50 border-amber-200", label: "Awaiting provider" },
  under_review: { icon: Shield, tone: "text-[#165a71] bg-[#165a71]/10 border-[#165a71]/30", label: "Under review" },
  escalated: { icon: Shield, tone: "text-rose-700 bg-rose-50 border-rose-200", label: "Escalated" },
  resolved_for_customer: { icon: CheckCircle2, tone: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Resolved for customer" },
  resolved_for_provider: { icon: CheckCircle2, tone: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Resolved for provider" },
  partial_settlement: { icon: CheckCircle2, tone: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Partial settlement" },
  closed: { icon: CheckCircle2, tone: "text-slate-600 bg-slate-50 border-slate-200", label: "Closed" },
};

export default function DisputesListPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useDocumentTitle("Disputes");

  useEffect(() => {
    api.get("/disputes/mine").then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  const openCount = items.filter((d) => !d.resolved_at).length;

  return (
    <DashboardShell>
      <div className="max-w-4xl">
        <div className="mb-6">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Resolution Center</div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Your disputes</h1>
          <p className="text-[#252A34]/70 text-sm mt-1">
            {openCount} open · {items.length - openCount} resolved. Provider payouts are frozen in a <b>reserved</b> bucket while a dispute is open.
          </p>
        </div>

        {loading ? (
          <div className="text-[#252A34]/60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <Shield className="h-6 w-6 mx-auto text-[#165a71]" />
            <div className="mt-2 font-display text-lg text-[#071b22] font-semibold">No disputes on file.</div>
            <div className="text-sm text-[#252A34]/70 mt-1">When something goes wrong on a booking or order, open a dispute from the transaction page.</div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="disputes-list">
            {items.map((d) => {
              const meta = STATUS_META[d.status] || STATUS_META.open;
              const role = user?.id === d.customer_id ? "customer" : "provider";
              return (
                <Link
                  key={d.id}
                  to={`/dashboard/disputes/${d.id}`}
                  className="block rounded-2xl border border-slate-200 bg-white p-4 md:p-5 hover:border-[#165a71]/40 transition-colors"
                  data-testid={`dispute-row-${d.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-wider text-[#252A34]/60">{d.kind.replace(/_/g, " ")} · Opened by {d.opened_role} · You are the {role}</div>
                      <div className="font-display font-semibold text-[#071b22] mt-1 truncate">{d.subject || "Dispute"}</div>
                      <div className="text-xs text-[#252A34]/60 mt-1">Opened {d.created_at ? new Date(d.created_at).toLocaleString() : ""}</div>
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full text-xs font-medium px-3 py-1 border ${meta.tone}`}>
                      <meta.icon className="h-3.5 w-3.5" /> {meta.label}
                    </span>
                  </div>
                  {d.reserved_amount > 0 && (
                    <div className="text-[11px] text-[#252A34]/60 mt-2">Reserved: <b>${d.reserved_amount.toFixed(2)}</b>{d.resolved_amount_refunded ? ` · Refunded: $${d.resolved_amount_refunded.toFixed(2)}` : ""}</div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
